import React, { useState } from 'react'
import {Share2, Facebook, Twitter, Link as LinkIcon, MessageCircleDashed as MessageCircle, Mail, Check} from 'lucide-react'

interface SocialShareProps {
  title: string
  url?: string
}

export function SocialShare({ title, url = window.location.href }: SocialShareProps) {
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [copied, setCopied] = useState(false)

  const shareText = `Check out this amazing magical cartoon: ${title} on MagicCartoon.com!`
  
  const socialLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'hover:bg-blue-600',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      bgColor: 'bg-blue-500'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'hover:bg-sky-500',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
      bgColor: 'bg-sky-400'
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'hover:bg-green-600',
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + url)}`,
      bgColor: 'bg-green-500'
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'hover:bg-gray-600',
      url: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText + '\n\n' + url)}`,
      bgColor: 'bg-gray-500'
    }
  ]

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleShare = (socialUrl: string) => {
    window.open(socialUrl, '_blank', 'width=600,height=400')
    setShowShareMenu(false)
  }

  return (
    <div className="relative">
      {/* Main Share Button */}
      <button
        onClick={() => setShowShareMenu(!showShareMenu)}
        className="group bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all flex items-center gap-2"
      >
        <Share2 className="w-5 h-5" />
        Share This
      </button>

      {/* Share Menu Dropdown */}
      {showShareMenu && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 w-72 z-50 border border-gray-100">
          <div className="mb-3">
            <h4 className="font-bold text-gray-800 mb-1">Share this magical content</h4>
            <p className="text-sm text-gray-600">Help others discover {title}</p>
          </div>

          {/* Social Media Buttons */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {socialLinks.map((social) => (
              <button
                key={social.name}
                onClick={() => handleShare(social.url)}
                className={`${social.bgColor} text-white px-4 py-3 rounded-xl font-semibold ${social.color} transition-all flex items-center justify-center gap-2 hover:scale-105`}
              >
                <social.icon className="w-5 h-5" />
                {social.name}
              </button>
            ))}
          </div>

          {/* Copy Link */}
          <div className="border-t border-gray-200 pt-3">
            <button
              onClick={handleCopyLink}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 text-green-500" />
                  Link Copied!
                </>
              ) : (
                <>
                  <LinkIcon className="w-5 h-5" />
                  Copy Link
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Overlay to close menu */}
      {showShareMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowShareMenu(false)}
        />
      )}
    </div>
  )
}
